import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import DialogContentText from '@mui/material/DialogContentText';

import { getSectionChoices, zoomablesOptions } from 'helpers';
import { Typeahead, LineLoader, Modal, RadioOptions } from '@timmons-group/shared-react-components';
import { PREF_STATE_KEY, ZOOMABLES_URL } from 'constants';
import { useLocalStorage, useStaleData, useIsModal } from '@timmons-group/shared-react-components/hooks';
// TODO: Get this to shared-react-components once DB is updated
import { useLayout } from 'hooks/useData';

/**
 * Modal housing the community area creation form
 * @param {*} modalClosed function to call when modal is closed
 * @returns
 */
const CommunityAreaModal = ({ modalClosed }) => {
  const nav = useNavigate();
  const { open, onCancel, setOpen } = useIsModal(modalClosed);
  const [zoomables, zoomablesLoading] = useStaleData(ZOOMABLES_URL);
  const [layout, layoutLoading] = useLayout('community', 'newModal');
  const [preferredState] = useLocalStorage(PREF_STATE_KEY, null);

  // Get the properties needed for hook form to handle validation and submit
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      state: '',
      type: null,
    },
  });

  // useMemo is used to prevent the refLayers / zoomables from being re-rendered every time
  const stateOptions = useMemo(() => {
    const options = zoomablesLoading || !zoomables ? [] : zoomablesOptions(zoomables, 'name');
    let selection = options[0] || null;

    // Used for the default selection on the state field if preferredState is set
    if (preferredState) {
      selection = options.find((o) => (o.value === preferredState.value && o.label === preferredState.label)) || selection;
    }
    setValue('state', selection);

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomablesLoading]);

  const typeOptions = useMemo(() => {
    return layoutLoading
      ? []
      : getSectionChoices(layout, 'New Community', 'communityType');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutLoading]);

  // Check if there was an error with the data after loading flags are false
  const loadingError = useMemo(() => {
    if (
      !zoomablesLoading &&
      !layoutLoading &&
      (typeOptions.length === 0 || stateOptions.length === 0)
    ) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomablesLoading, layoutLoading]);

  // Handle the form submission by closing modal and navigating to the planning area
  const onSubmit = (data) => {
    setOpen(false);

    // Navigate to the area if the form is valid
    if (!loadingError) {
      nav(
        `/pam/communityArea/create?state=${data.state.value}&type=${data.type}`
      );
    }
  };

  /**
   * React hook form controller properties and pass / map as needed
   * @returns jsx
   */
  const renderRadio = ({
    field: { value, onChange },
    fieldState: { error },
  }) => {
    // so we need to manually connect a few props here for react hook form
    return (
      <RadioOptions
        items={typeOptions}
        isRequired={true}
        id="type"
        label="Create Community Area by"
        value={value}
        onChange={onChange}
        error={error}
      />
    );
  };

  /**
   * Render the state typeahead
   * @param {*} field, fieldState, formState, and ref are passed in via react hook form
   * @returns
   */
  const renderState = ({
    field,
    field: { onChange },
    fieldState: { error },
  }) => {
    // Value is passed in via the react hook form inside of field
    // Ref is needed by the typeahead / autoComplete component and is passed in via props spreading
    return (
      // We need to manually connect a few props here for react hook form
      <Typeahead
        {...field}
        items={stateOptions}
        label="State"
        isRequired={true}
        // These are props that are passed to the MUI TextField rendered by Typeahead
        textFieldProps={{
          placeholder: 'Select a State',
          error: !!error,
          helperText: error?.message,
        }}
        // hooks-form appears to only want value and not the native onChange
        onChange={(_, newValue) => onChange(newValue)}
        error={error}
      />
    );
  };

  const theForm = () => {
    if (layoutLoading || zoomablesLoading) {
      return <LineLoader message="Loading Options..." />;
    }

    if (loadingError) {
      return <>There was an error loading options. Please try again later.</>;
    }

    return (
      <form>
        {/* State hook-form controller */}
        <Controller
          control={control}
          rules={{ required: 'State is required' }}
          name="state"
          render={renderState}
        />
        <br />
        {/* Type radio options hook-form controller */}
        <Controller
          control={control}
          name="type"
          rules={{ required: 'Community Area Type is required' }}
          render={renderRadio}
        />
      </form>
    );
  };

  // handleSubmit(onSubmit) returns a function that will be called when the form is submitted
  // handleSubmit(onSubmit)()
  return (
    <Modal
      title="Add Community"
      open={open}
      okLabel="Continue"
      onOk={handleSubmit(onSubmit)}
      onCancel={onCancel}
      hideActions={layoutLoading || zoomablesLoading}
    >
      <DialogContentText>
        Community areas should show where hazard mitigation plans have been
        developed, or are in development. A Community&apos;s area will be based
        on a single place (e.g., county), or a collection of adjacent places,
        depending on the extent of plans associated to it. Select a state and
        type of place to get started.
      </DialogContentText>
      <br />
      {theForm()}
    </Modal>
  );
};

CommunityAreaModal.propTypes = {
  modalClosed: PropTypes.func.isRequired,
};

export default CommunityAreaModal;
